import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Plus, Trash2, Eye, Edit } from 'lucide-react';
import PageEditModal from './PageEditModal';
import SystemPageEditModal from './SystemPageEditModal';

const PagesTab = ({ 
  pages, 
  systemPages, 
  onDeletePage, 
  onSavePage, 
  onSaveSystemPage, 
  saving 
}) => {
  const [editingPage, setEditingPage] = useState(null);
  const [showPageModal, setShowPageModal] = useState(false);
  const [editingSystemPage, setEditingSystemPage] = useState(null);

  return (
    <>
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Page Management</h2>
          <Button 
            onClick={() => { setEditingPage(null); setShowPageModal(true); }} 
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Page
          </Button>
        </div>

        {pages.length === 0 ? (
          <p className="text-zinc-400 text-center py-8">No pages yet. Click "Add Page" to create one.</p>
        ) : (
          <div className="space-y-3">
            {pages.map((page) => (
              <div key={page.id} className="bg-zinc-800 rounded-lg p-4 flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-white font-medium">{page.title}</h3>
                  <p className="text-zinc-400 text-sm">/page/{page.slug}</p>
                  <div className="flex gap-2 mt-2">
                    {page.show_in_menu && <Badge className="bg-blue-500/20 text-blue-400">Menu</Badge>}
                    {page.show_in_footer && <Badge className="bg-purple-500/20 text-purple-400">Footer</Badge>}
                    {!page.active && <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/page/${page.slug}`, '_blank')}
                    className="border-blue-600 text-blue-400"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingPage(page); setShowPageModal(true); }}
                    className="border-yellow-600 text-yellow-400"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-red-600 text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Page</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                          Are you sure you want to delete "{page.title}"?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => onDeletePage(page.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System Pages Section */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6 mt-6">
        <h2 className="text-xl font-semibold text-white mb-6">System Pages (SEO Settings)</h2>
        <p className="text-zinc-400 text-sm mb-4">Configure SEO settings for built-in pages. These pages cannot be deleted.</p>
        
        <div className="space-y-3">
          {[
            { key: 'home', title: 'Homepage', url: '/', icon: '🏠' },
            { key: 'search', title: 'Search / Listings', url: '/freelancers', icon: '🔍' },
            { key: 'contact', title: 'Contact Page', url: '/contact', icon: '📧' },
            { key: 'create_listing', title: 'Add Listing', url: '/create-listing', icon: '📝' }
          ].map((sysPage) => (
            <div key={sysPage.key} className="bg-zinc-800 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{sysPage.icon}</span>
                <div>
                  <h3 className="text-white font-medium">{sysPage.title}</h3>
                  <p className="text-zinc-400 text-sm">{sysPage.url}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(sysPage.url, '_blank')}
                  className="border-blue-600 text-blue-400"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditingSystemPage({ key: sysPage.key, ...systemPages[sysPage.key] })}
                  className="border-yellow-600 text-yellow-400"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Page Modal */}
      {showPageModal && (
        <PageEditModal
          page={editingPage}
          onSave={(data) => {
            onSavePage(data, editingPage);
            setShowPageModal(false);
            setEditingPage(null);
          }}
          onClose={() => { setShowPageModal(false); setEditingPage(null); }}
          saving={saving}
        />
      )}

      {/* System Page Edit Modal */}
      {editingSystemPage && (
        <SystemPageEditModal
          pageKey={editingSystemPage.key}
          pageData={editingSystemPage}
          onSave={onSaveSystemPage}
          onClose={() => setEditingSystemPage(null)}
          saving={saving}
        />
      )}
    </>
  );
};

export default PagesTab;
